import { Button, message, Space, Typography } from 'antd';
import classNames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { useReportPage } from '@/infrastructure/reporter/datong/report';
import { useAuthContext } from '@/presentation/hooks';
import { LiteLayout } from '@/presentation/layouts/lite';
import {
  LoginType,
  SubSystemType,
  TencentCloudAccount,
  TencentCloudAccountOwnerInfo,
  TencentCloudToken,
} from '@/presentation/pages/login/types';
import { authService } from '@/service/auth';
import { tencentCloudBFFService } from '@/service/tencent-cloud';
import styles from './index.module.scss';

const { Title } = Typography;

export interface SelectAccountProps {
  accountList: TencentCloudAccount[];
  token: TencentCloudToken;
}

const AccountGroup = (props: {
  type: 'main' | 'sub',
  accountList: TencentCloudAccount[];
  uin: string;
  onChange?: (account: TencentCloudAccount) => void;
  lastLoginUin?: string;
}) => {
  if (!props.accountList || isEmpty(props.accountList)) return null;

  return (
    <div className={styles.accountGroup}>
      <Title level={4} className={styles.title}>{props.type === 'main' ? '主账号' : '子账号'}</Title>
      <Space direction={'vertical'}>
        {(props.accountList || []).map(account => <AccountItem
            account={account}
            isActive={`${props.uin}` === `${account.uin}`}
            onChange={props.onChange}
            type={props.type}
            lastLoginUin={props.lastLoginUin}
          />)}
      </Space>
    </div>
  );
};

const AccountItem = (props: {
  account: TencentCloudAccount,
  isActive: boolean,
  onChange?: (account: TencentCloudAccount) => void;
  type: 'main' | 'sub',
  lastLoginUin?: string;
}) => {
  const ownerInfo = get(props.account, 'ownerInfo[0]', null) as TencentCloudAccountOwnerInfo;
  const isLastLoginAccount = props.lastLoginUin && props.lastLoginUin === `${props.account.uin}`;

  return (
    <div className={classNames({
      [styles.accountItem]: true,
      [styles.accountItemActive]: props.isActive,
    })}
         onClick={() => {
           if (props.isActive) return;
           props.onChange?.(props.account);
         }}
    >
      {props.type === 'main'
        ? ownerInfo.displayName
        : `${ownerInfo.subUserName}(${props.account.uin})属于${ownerInfo.displayName}`
      }
      {isLastLoginAccount && <div className={styles.lastLogin}>上次登录</div>}
    </div>
  );
};

const SelectAccount = () => {
  const { state } = useLocation();
  const { accountList = [], token } = state as SelectAccountProps || {};
  const { onLoginSuccess, authInfo } = useAuthContext();
  const lastLoginUin = authInfo?.lastLoginUin;
  const [innerToken, setInnerToken] = useState<TencentCloudToken>(token);
  const [targetAccount, setTargetAccount] = useState<TencentCloudAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [changing, setChaging] = useState(false);
  useReportPage(PAGE_ID.selectAccount);

  const activeUin = targetAccount?.uin || token.uin;

  // 主账号
  const mainAccounts = useMemo(
    () => accountList.filter(account => account.ownerInfo[0].subSystemType !== SubSystemType.SubAccount),
    [accountList],
  );

  // 子账号
  const subAccounts = useMemo(
    () => accountList.filter(account => account.ownerInfo[0].subSystemType === SubSystemType.SubAccount),
    [accountList],
  );

  const onSelected = async (account: TencentCloudAccount) => {
    setChaging(true);
    setLoading(true);
    const changedToken: unknown = await tencentCloudBFFService.change({
      token: innerToken.key,
      clientUA: navigator.userAgent,
      tokenUin: innerToken.uin,
      tokenOwnerUin: innerToken.ownerUin,
      tokenChangeUin: account?.uin as string,
      tokenChangeOwnerUin: account?.ownerInfo[0].uin as string,
      type: 'wx',
    });
    setChaging(false);
    setLoading(false);

    if (changedToken) {
      setInnerToken(changedToken as TencentCloudToken);
      setTargetAccount(account);
    } else {
      message.error('账号切换失败，请稍后再试！');
    }
  };

  const submitLogin = async () => {
    setLoading(true);
    const res = await authService.login(LoginType.TencentCloud, {
      saasToken: innerToken.key,
      uin: innerToken.uin,
      ownerUin: innerToken.ownerUin,
    });

    setLoading(false);
    if (res) {
      onLoginSuccess(LoginType.TencentCloud, res?.userInfo, {
        ...innerToken,
        buglyToken: res?.buglyToken,
        saasToken: innerToken.key,
      });
    } else {
      message.error('登录失败，请稍后再试！');
    }
  };

  return (
    <LiteLayout>
      <div className={styles.selectAccount}>
        <Title level={4} className={styles.title}>请选择登录帐号</Title>
        <p className={styles.desc}>当前登录方式有<span className={styles.count}>{accountList.length}个</span>关联帐户，请选择登录账号</p>

        <AccountGroup
          type={'main'}
          accountList={mainAccounts}
          uin={activeUin}
          onChange={onSelected}
          lastLoginUin={lastLoginUin}
        />
        <AccountGroup
          type={'sub'}
          accountList={subAccounts}
          uin={activeUin}
          onChange={onSelected}
          lastLoginUin={lastLoginUin}
        />

        <Button
          className={styles.submitChange}
          loading={loading}
          size={'large'}
          type={'primary'}
          onClick={submitLogin}
        >{changing ? '账号切换中' : '登录'}</Button>
      </div>
    </LiteLayout>
  );
};

export default SelectAccount;
